/**
 * CreateClientPage — Formulaire de création d'un nouveau client
 *
 * 3 sections : Identité, Données de naissance, Notes.
 * Appelle POST /api/clients puis redirige vers la fiche du client.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiFetch } from '../utils/api.js';
import { colors } from '../styles/tokens.js';
import type { ClientDetail } from '../types/clients.js';

interface ClientForm {
  prenom: string;
  nom: string;
  email: string;
  telephone: string;
  dateNaissance: string;
  heureNaissance: string;
  lieuNaissance: string;
  paysNaissance: string;
  latitude: string;
  longitude: string;
  notes: string;
}

const INITIAL: ClientForm = {
  prenom: '',
  nom: '',
  email: '',
  telephone: '',
  dateNaissance: '',
  heureNaissance: '',
  lieuNaissance: '',
  paysNaissance: 'France',
  latitude: '',
  longitude: '',
  notes: '',
};

export function CreateClientPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<ClientForm>(INITIAL);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function set(field: keyof ClientForm, value: string) {
    setForm((f) => ({ ...f, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((e) => { const next = { ...e }; delete next[field]; return next; });
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    /** Validation locale */
    const errs: Record<string, string> = {};
    if (!form.prenom.trim()) errs.prenom = 'Le prénom est requis';
    if (!form.nom.trim()) errs.nom = 'Le nom est requis';
    if (!form.dateNaissance) errs.dateNaissance = 'La date de naissance est requise';
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }

    setIsLoading(true);

    const body: Record<string, unknown> = {
      prenom: form.prenom.trim(),
      nom: form.nom.trim(),
      dateNaissance: form.dateNaissance,
    };

    if (form.email.trim()) body.email = form.email.trim();
    if (form.telephone.trim()) body.telephone = form.telephone.trim();
    if (form.heureNaissance.trim()) body.heureNaissance = form.heureNaissance.trim();
    if (form.lieuNaissance.trim()) body.lieuNaissance = form.lieuNaissance.trim();
    if (form.paysNaissance.trim()) body.paysNaissance = form.paysNaissance.trim();
    if (form.latitude.trim()) body.latitude = parseFloat(form.latitude);
    if (form.longitude.trim()) body.longitude = parseFloat(form.longitude);
    if (form.notes.trim()) body.notes = form.notes.trim();

    const res = await apiFetch<ClientDetail>('/clients', {
      method: 'POST',
      body: JSON.stringify(body),
    });

    if (res.success && res.data) {
      navigate(`/clients/${res.data.id}`);
    } else {
      if (res.details) {
        setFieldErrors(res.details);
      }
      setError(res.error ?? 'Impossible de créer le client');
    }

    setIsLoading(false);
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 14px',
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    fontSize: '0.95rem',
    color: colors.text,
    background: '#fff',
    outline: 'none',
    fontFamily: 'inherit',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: colors.text,
    marginBottom: 6,
  };

  const sectionStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: 16,
    paddingBottom: 8,
    borderBottom: `1px solid ${colors.border}`,
  };

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    borderRadius: 10,
    border: `1px solid ${colors.border}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    padding: 24,
  };

  function renderField(
    field: keyof ClientForm,
    label: string,
    type: string = 'text',
    opts?: { placeholder?: string; required?: boolean; half?: boolean },
  ) {
    const hasError = !!fieldErrors[field];
    return (
      <div style={opts?.half ? { flex: '1 1 calc(50% - 8px)', minWidth: 160 } : {}}>
        <label style={labelStyle}>
          {label}
          {opts?.required && <span style={{ color: colors.accent, marginLeft: 4 }}>*</span>}
        </label>
        <input
          type={type}
          value={form[field]}
          onChange={(e) => set(field, e.target.value)}
          placeholder={opts?.placeholder}
          style={{
            ...inputStyle,
            borderColor: hasError ? colors.danger : colors.border,
          }}
          onFocus={(e) => { (e.target as HTMLInputElement).style.borderColor = colors.accent; }}
          onBlur={(e) => { (e.target as HTMLInputElement).style.borderColor = hasError ? colors.danger : colors.border; }}
        />
        {hasError && (
          <div style={{ fontSize: 12, color: colors.danger, marginTop: 4 }}>{fieldErrors[field]}</div>
        )}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 700, width: '100%' }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 24, fontSize: 13, color: colors.textMuted }}>
        <Link to="/clients" style={{ color: colors.textMuted, textDecoration: 'none' }}>
          Clients
        </Link>
        <i className="bi bi-chevron-right" style={{ fontSize: 10 }} />
        <span style={{ color: colors.accent }}>Nouveau client</span>
      </div>

      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
        <i className="bi bi-person-plus" style={{ fontSize: 20, color: colors.accent }} />
        <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 600, color: colors.text }}>
          Nouveau client
        </h1>
      </div>

      {/* Erreur globale */}
      {error && (
        <div
          style={{
            background: '#fef2f2',
            border: '1px solid #fca5a5',
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 14,
            color: '#991b1b',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <i className="bi bi-exclamation-circle" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Section 1 : Identité */}
        <div style={{ ...cardStyle, marginBottom: 20 }}>
          <div style={sectionStyle}>
            <i className="bi bi-person" style={{ fontSize: 12, marginRight: 6 }} />
            Identité
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
            {renderField('prenom', 'Prénom', 'text', { placeholder: 'Ex : Marie', required: true, half: true })}
            {renderField('nom', 'Nom', 'text', { placeholder: 'Ex : Dupont', required: true, half: true })}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {renderField('email', 'Email', 'email', { placeholder: 'marie.dupont@email.com', half: true })}
            {renderField('telephone', 'Téléphone', 'tel', { placeholder: '06 12 34 56 78', half: true })}
          </div>
        </div>

        {/* Section 2 : Données de naissance */}
        <div style={{ ...cardStyle, marginBottom: 20 }}>
          <div style={sectionStyle}>
            <i className="bi bi-stars" style={{ fontSize: 12, marginRight: 6 }} />
            Données de naissance
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
            {renderField('dateNaissance', 'Date de naissance', 'date', { required: true, half: true })}
            {renderField('heureNaissance', 'Heure de naissance', 'time', { half: true })}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
            {renderField('lieuNaissance', 'Lieu de naissance', 'text', { placeholder: 'Ex : Paris', half: true })}
            {renderField('paysNaissance', 'Pays', 'text', { placeholder: 'France', half: true })}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {renderField('latitude', 'Latitude', 'number', { placeholder: '48.856614', half: true })}
            {renderField('longitude', 'Longitude', 'number', { placeholder: '2.352222', half: true })}
          </div>
          <p style={{ margin: '12px 0 0', fontSize: 12, color: colors.textMuted, fontStyle: 'italic' }}>
            Les coordonnées GPS sont optionnelles — elles sont généralement fournies par Olympia.
          </p>
        </div>

        {/* Section 3 : Notes */}
        <div style={{ ...cardStyle, marginBottom: 24 }}>
          <div style={sectionStyle}>
            <i className="bi bi-journal-text" style={{ fontSize: 12, marginRight: 6 }} />
            Notes
          </div>
          <textarea
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            rows={4}
            placeholder="Notes personnelles sur le client..."
            style={{
              ...inputStyle,
              resize: 'vertical',
            }}
            onFocus={(e) => { (e.target as HTMLTextAreaElement).style.borderColor = colors.accent; }}
            onBlur={(e) => { (e.target as HTMLTextAreaElement).style.borderColor = colors.border; }}
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 24px',
              borderRadius: 6,
              border: 'none',
              background: colors.accent,
              color: colors.text,
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.5 : 1,
              fontFamily: 'inherit',
            }}
          >
            {isLoading ? (
              <i className="bi bi-arrow-repeat" style={{ fontSize: 14, animation: 'spin 1s linear infinite' }} />
            ) : (
              <i className="bi bi-check-lg" style={{ fontSize: 14 }} />
            )}
            {isLoading ? 'Création...' : 'Créer le client'}
          </button>
          <Link
            to="/clients"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 20px',
              borderRadius: 6,
              border: `1px solid ${colors.border}`,
              background: 'transparent',
              color: '#666',
              fontSize: '0.95rem',
              textDecoration: 'none',
              fontFamily: 'inherit',
            }}
          >
            Annuler
          </Link>
        </div>
      </form>
    </div>
  );
}
